import type { FieldProps } from '@rjsf/utils'
import { useSkrottlappScan } from '../../../hooks/useSkrottlappScan'
import { Mtds, QrCodeIcon } from 'prove-common'
import { useState } from 'react'
import { CodeScanModal } from '../../code-scan-modal/CodeScanModal'
import { ErrorMessage } from '../../error-message/ErrorMessage'
import { pushMatomoEvent } from '../../../utils/matomoUtils'
import { type Activity } from '../../../types/types'

function SkrottlappScanField({
  registry,
  fieldPathId,
  disabled,
  activity
}: Readonly<FieldProps & { activity: Activity }>) {
  const formContext = registry.formContext
  const isFetching = formContext?.isFetching ?? false
  const [isScanOpen, setIsScanOpen] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const skrottlappScan = useSkrottlappScan()
  const isSubSample = fieldPathId.$id.includes('subSamples')
  if (skrottlappScan?.hasScanned) return null
  if (formContext == null || (skrottlappScan == null && isSubSample)) {
    if (!error) {
      setError(new Error('Det har oppstått en feil med skrottlapp-skanneren'))
    }
  }
  return (
    <Mtds.Grid>
      {!error && (
        <Mtds.Button
          aria-busy={isFetching}
          data-variant='primary'
          disabled={disabled}
          onClick={() => {
            setIsScanOpen(true)
            pushMatomoEvent('skrottlapp_scanner', 'opened')
          }}
        >
          <QrCodeIcon />
          Skann skrottlapp
        </Mtds.Button>
      )}
      {error && <ErrorMessage error={error} />}

      {isScanOpen && (
        <CodeScanModal
          type='skrottlapp'
          isScanOpen={isScanOpen}
          setIsScanOpen={setIsScanOpen}
          onCodeScanned={(code, source) => {
            const fieldId = fieldPathId.$id
            setIsScanOpen(false)
            if (isSubSample) {
              // Validate subsample index in fieldId matches index
              const index = skrottlappScan?.index
              const idIndex = Number(
                /subSamples_(\d+)_payload/.exec(fieldId)?.[1] ?? Number.NaN
              )
              if (!Number.isInteger(idIndex) || idIndex !== index) {
                pushMatomoEvent('skrottlapp_scanner', 'scan_error')
                setError(
                  new Error('Det har oppstått en feil med skrottlapp-skanneren')
                )
                return
              }
            }
            pushMatomoEvent(
              'skrottlapp_scanner',
              source === 'scan' ? 'scan_success' : 'manual_success'
            )
            formContext.onScanSkrottlapp(code, fieldId, activity)
          }}
        />
      )}
    </Mtds.Grid>
  )
}

export function SkrottlappScanFieldStorfe(props: Readonly<FieldProps>) {
  return <SkrottlappScanField {...props} activity='AKTIVITET$STORFEHOLD' />
}

export function SkrottlappScanFieldSvin(props: Readonly<FieldProps>) {
  return <SkrottlappScanField {...props} activity='AKTIVITET$SVINEHOLD' />
}

export function SkrottlappScanFieldSau(props: Readonly<FieldProps>) {
  return <SkrottlappScanField {...props} activity='AKTIVITET$SAUEHOLD' />
}

export function SkrottlappScanFieldRein(props: Readonly<FieldProps>) {
  return <SkrottlappScanField {...props} activity='AKTIVITET$TAMREINDRIFT' />
}
